import { Injectable, signal } from '@angular/core';

/**
 * Service zum Laden von Tiles aus einer MBTiles-Datei (SQLite).
 * Nutzt sql.js (über die bereits vorhandene GeoPackage-Dependency).
 */
@Injectable({ providedIn: 'root' })
export class MBTilesService {
	private db: any;
	private tileCache = new Map<string, string>(); // key -> data URL
	readonly ready = signal(false);
	readonly loading = signal(false);

	/**
	 * Lädt eine MBTiles-Datei von einer URL und öffnet sie.
	 */
	async load(url: string): Promise<void> {
		if (this.db || this.loading()) return;
		this.loading.set(true);
		try {
			// MBTiles-Datei herunterladen
			const response = await fetch(url);
			if (!response.ok) throw new Error(`MBTiles fetch failed: ${response.status}`);
			const buffer = await response.arrayBuffer();

			// sql.js initialisieren (nutzt das bereits vorhandene WASM)
			const initSqlJs = (await import('sql.js')).default;
			const SQL = await initSqlJs({
				locateFile: (file: string) => `/assets/sql/${file}`
			});
			this.db = new SQL.Database(new Uint8Array(buffer));
			this.ready.set(true);
		} catch (err) {
			console.warn('[MBTiles] Laden fehlgeschlagen:', err);
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * Liest ein Tile aus der MBTiles-Datei.
	 * MBTiles verwendet TMS y-Koordinate (gespiegelt).
	 * @returns data URL oder undefined
	 */
	getTile(z: number, x: number, y: number): string | undefined {
		// Cache prüfen
		const key = `${z}_${x}_${y}`;
		const cached = this.tileCache.get(key);
		if (cached) return cached;

		if (!this.db) return undefined;

		// MBTiles y ist TMS (umgekehrt zu Slippy-Map/OSM)
		const yTms = (1 << z) - 1 - y;

		try {
			const stmt = this.db.prepare('SELECT tile_data FROM tiles WHERE zoom_level = ? AND tile_column = ? AND tile_row = ?');
			stmt.bind([z, x, yTms]);
			if (stmt.step()) {
				const row = stmt.get();
				const blob = row[0] as Uint8Array;
				// Blob zu Data-URL konvertieren
				const dataUrl = this.uint8ToDataUrl(blob, 'image/png');
				this.tileCache.set(key, dataUrl);
				stmt.free();
				return dataUrl;
			}
			stmt.free();
		} catch (err) {
			// Tile nicht gefunden — normal für Bereiche außerhalb der Abdeckung
		}
		return undefined;
	}

	private uint8ToDataUrl(data: Uint8Array, mime: string): string {
		let binary = '';
		for (let i = 0; i < data.length; i++) {
			binary += String.fromCharCode(data[i]);
		}
		return `data:${mime};base64,${btoa(binary)}`;
	}
}
